import React, { Component, Fragment } from 'react';
import { Grid, Switch, Select, Tooltip, ListItem, ListItemIcon, IconButton,TextField } from '@material-ui/core';
import { Delete, AddCircle, HelpOutline, PauseCircleOutline, Block } from '@material-ui/icons';
import WeekdayPicker from './weekdayPicker';
import TimezonePicker from './timezonePicker';
import { MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import TranslationEditor from './translationEditor';
import ConditionEditor from './conditionEditor';


const defaultRules = {
  enabled: false,
  header: "Meeting Policy Hints",
  notificationError: false,
  notificationSuccess: false,
  agendaHtml: { enabled: false, text: "Please add a hosted agenda (sharepoint.com).", value: "%5E%28%28%3F%21sharepoint%5C.com%5C%2F%29%5B%5Cs%5CS%5D%29%2A%24" },
  agendaText: { enabled: false, text: "Please consider adding an agenda which is longer than 50 characters.", value: "%5E%5B%5Cs%5CS%5D%7B0%2C50%7D%24" },
  duration: { enabled: true, text: "Please consider shortening the length of the meeting.", value: 60 },
  participants: { enabled: true, text: "Please consider inviting less participants.", value: 5, },
  recurrenceEnds: { enabled: false, text: "Too long running recurring meeting. Please set end date to less than 90 days from now.", value: 90 },
  requiredParticipants: { enabled: false, text: "Many required participants. Please use optional field.", value: 3 },
  shortening: { enabled: false, text: "Please allow break in back to back meetings and consider configuring default shortening to your Outlook settings." },
  time: { enabled: true, text: "Please consider shortening the length of the meeting or removing some of the meeting partcipants.", value: 240 },
  time90: { enabled: true, text: "Please consider reducing the frequency of recurrence.", value: 960 },
  rooms: { enabled: false, text: "Please refrain booking multiple meetings rooms for your meeting.", value: 1 },
  schedule: {
    enabled: false,
    limits: [
      { enabled:false, duration: 480, startTime: "00:00:00.000Z", text: "We discourage meetings before 08:00", weekday: [ 1, 2, 3, 4, 5 ] },
      { enabled:false, duration: 360, startTime: "12:00:00.000Z", text: "Meeting free afternoons on Wednesdays", weekday: [ 3 ] },
      { enabled:false, duration: 480, startTime: "00:00:00.000Z", text: "New York - Out of Office (<8am)", timezone: "America/New_York", weekday: [ 1, 2, 3, 4, 5 ] },
      { enabled:false, duration: 300, startTime: "18:00:00.000Z", text: "New York - Out of Office (>6pm)", timezone: "America/New_York", weekday: [ 1, 2, 3, 4, 5 ] },
      { enabled:false, duration: 480, startTime: "00:00:00.000Z", text: "UK - Out of  Office (<8am)", timezone: "Europe/London", weekday: [ 1, 2, 3, 4, 5 ] },
      { enabled:false, duration: 360, startTime: "18:00:00.000Z", text: "UK - Out of Office (>6pm)", timezone: "Europe/London", weekday: [ 1, 2, 3, 4, 5 ] }
    ]
  },
};

const availableRules = [
  { name: "duration", desc:"Duration", type: "minutes", explain:"This rule can be used to validate the duration of the meeting." },
  { name: "participants", desc:"Participants", type: "number", explain:"This rule can be used to validate the total number of participants (required + optional) of the meeting (organizer + invitees)." },
  { name: "requiredParticipants", desc:"Required Participants", type: "number", explain:"This rule can be used to validate the total number of required participants of the meeting (organizer + invitees)." },
  { name: "time", desc:"Time", type: "minutes", explain:"This rule can be used to validate the total time of the meeting [(organizer + invitees) * duration] of the meeting." },
  { name: "time90", desc:"Time in quarter", type: "minutes", explain:"This rule can be used to validate the total time of the meeting in a quarter [(organizer + invitees) * duration * occurences in coming 90 days] of the meeting." },
  { name: "shortening", desc:"Shorter meetings", type: "boolean", explain:"This rule can be used to remind people to shorten their meetings. It is shown if meeting ends at XX:00, or XX:30." },
  { name: "agendaHtml", desc:"Agenda (HTML)", type: "regex", explain:"This rule can be used to validate contents of the HTML formatted agenda field. Field is automatically scrubbed from known video call invite details. Accepts standard regex rules." },
  { name: "agendaText", desc:"Agenda (TEXT)", type: "regex", explain:"This rule can be used to validate contents of the text formatted agenda field. Field is automatically scrubbed from known video call invite details. Accepts standard regex rules." },
  { name: "recurrenceEnds", desc:"Recurrence End", type: "number", explain:"This rule can be used to validate how long the recurring meeting is scheduled to run." },
  { name: "rooms", desc:"Room Bookings", type: "number", explain:"This rule can be used to remind organizer to be considerate with meeting room bookings." },
];

class AppSettingMeetingPolicy extends Component {
  constructor(props) {
    super(props);
    this.state = { };
  }


  // Used to handle switches on rules and settings
  handleBooleanChange = (event) => {
    const clicked = event.target.name.split(".");
    const rules = this.getAvailableRulesForAccount();
    if(clicked.length === 3)
      rules[clicked[0]][clicked[1]][clicked[2]] = event.target.checked;
    if(clicked.length === 2)
      rules[clicked[0]][clicked[1]] = event.target.checked;
    if(clicked.length === 1)
      rules[clicked[0]] = event.target.checked;
    return this.props.updateRules(rules);
  }

  getAvailableRulesForAccount = () => {
    const rules = this.props.rules ? this.props.rules : defaultRules;

    // Populate missing rules
    Object.keys(defaultRules).forEach(key => {
      if(rules[key] === undefined) {
        rules[key] = defaultRules[key];
        // Check if we are adding a rule to existing rules, which is enabled by default
        if(this.props.rules && typeof rules[key] === "object" && rules[key].enabled === true) {
          rules[key].enabled = false;
          console.log("Disable", key, "because we have existing rules in place, and this is by default enabled, and this is not being executed as it stands.")
        }
      }
    })
    return rules;
  }

  // Used to update combobox and text field values
  updateValue = (event) => {
    const index = event.target.name.includes("|") ? event.target.name.split("|")[1] : null;
    const clicked = event.target.name.split(".");
    const rules = this.getAvailableRulesForAccount();

    let value;
    if(event.target.type === "number")
      value = parseInt(event.target.value, 10);
    else if(event.target.type === "text")
      value = event.target.value;
    else if(event.target.type === "regex")
      value = encodeURIComponent(event.target.value);
    else if(event.target.type === "checkbox")
      value = event.target.checked;
    else
      value = event.target.value;

    if(clicked.length === 3 && index !== null)
      rules[clicked[1]][clicked[2].split("|")[0]][index] = value;
    else if(clicked.length === 3)
      rules[clicked[1]][clicked[2]] = value;
    else if(clicked.length === 2)
      rules[clicked[1]] = value;
    else
      return console.warning("updateValue:", event.target.name, event.target.value);
    return this.props.updateRules(rules);
  }

  updateScheduleLimit = (event) => {
    const rules = this.getAvailableRulesForAccount();
    const limitIndex = event.currentTarget.name.split("|")[1];
    const variable = event.currentTarget.name.split("|")[2];

    if(variable === "timezone") {
      if(event.currentTarget.value)
        rules.schedule.limits[limitIndex][variable] = event.currentTarget.value;
      else
        delete rules.schedule.limits[limitIndex][variable];
    } else if(event.currentTarget.type === "time") {
      const dateValue = event.currentTarget.value;
      const hours = dateValue.getHours()
      const minutes = dateValue.getMinutes()
      const value = `${hours > 9 ? hours : "0" + hours}:${minutes > 9 ? minutes : "0" + minutes}:00.000Z`
      rules.schedule.limits[limitIndex][variable] = value;
    }
    else if(event.currentTarget.type === "text") {
      rules.schedule.limits[limitIndex][variable] = event.currentTarget.value;
    }
    else if(event.currentTarget.name.split("|").length === 4) {
      const value = parseInt(event.currentTarget.name.split("|")[3], 10);

      if(!rules.schedule.limits[limitIndex][variable]) 
        rules.schedule.limits[limitIndex][variable] = [value];
      else if(rules.schedule.limits[limitIndex][variable].includes(value)) 
        rules.schedule.limits[limitIndex][variable] = rules.schedule.limits[limitIndex][variable].filter(v => v !== value);
      else
        rules.schedule.limits[limitIndex][variable].push(value)
    } else if(event.currentTarget.checked !== undefined) {
      rules.schedule.limits[limitIndex][variable] = event.currentTarget.checked;
    } else {
      try {
        const number = parseInt(event.currentTarget.value, 10);
        rules.schedule.limits[limitIndex][variable] = number;
      } catch (e) {
        
      }
    }
    return this.props.updateRules(rules);
  }

  addSchedule = () => {
    const rules = this.getAvailableRulesForAccount();
    rules.schedule.limits.push({enabled:false, duration:60, startTime: "00:00:00.000Z", text:"Time rule to show.", weekday: [ 1, 2, 3, 4, 5 ]});
    this.props.updateRules(rules);
  }
  deleteSchedule= (removeIndex) => {
    const rules = this.getAvailableRulesForAccount();
    rules.schedule.limits = rules.schedule.limits.filter((i, index) => index !== removeIndex);
    this.props.updateRules(rules);
  }

  updateTranslationEditor = (rule, i18, index) => {
    const rules = this.getAvailableRulesForAccount();
    // Supports rule, and limit translation udpates
    if(!i18) {
      if(rule === "header")
        delete rules.i18[rule];
      else if(index !== undefined)
        delete rules[rule].limits[index].i18;
      else
        delete rules[rule].i18;
    } else {
      if(rule === "header")
        rules.i18 = { ...rules.i18 ? rules.i18 : {}, header: i18 };
      else if(index !== undefined)
        rules[rule].limits[index].i18 = i18;
      else
        rules[rule].i18 = i18;
    }
     
    return this.props.updateRules(rules);
  }
  updateConditionsEditor = (rule, conditions, index) => {
    const rules = this.getAvailableRulesForAccount();
    // Supports rule, and limit translation udpates
    if(!conditions) {
      if(index !== undefined)
        delete rules[rule].limits[index].conditions;
      else
        delete rules[rule].conditions;
    } else {
      if(index !== undefined)
        rules[rule].limits[index].conditions = conditions;
      else
        rules[rule].conditions = conditions;
    }
     
    return this.props.updateRules(rules);
  };

  render() {
    if (this.props.loading)
      return this.props.loading();

    const rules = this.getAvailableRulesForAccount();

    const startTimePicker = (value, name, disabled) => {
      return <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <TimePicker
              disabled={disabled}
              ampm={false}
              value={Date.parse("2020-01-01T" + value.split("Z")[0] + "+00:00")}
              onChange={(value) => this.updateScheduleLimit({currentTarget:{name, type:"time", value}})}
              format={"HH:mm"}
              minutesStep={15}
              />
            </MuiPickersUtilsProvider>
    }
    const getHoursAndMinutes = (val, disabled) => {
      const hours = Math.floor(val / 60);
      const minutes = val % 60;
      let timeString;
      if(hours && minutes)
        timeString = `${hours}h ${minutes}m`;
      else if (hours)
        timeString = `${hours}h`;
      else
      timeString = `${minutes}m`;
      if(disabled)
        return <div className="opacity-3"> { timeString } </div>
      return timeString;
    }

    const durationPicker = (value, name, disabled) => {
      const options = [30, 60, 90, 120, 150, 180, 240, 300, 360, 420, 480, 540, 600, 660, 720, 780, 840, 900, 960, 1020, 1080, 1140, 1200, 1260, 1320, 1380, 1440 ]
      const selectOptions = options.map(o => <option key={"duration-"+o} value={o}>{getHoursAndMinutes(o)}</option>)
      return <Select native onChange={this.updateScheduleLimit} value={value} name={name} disabled={disabled} >{selectOptions}</Select>
    }

    const renderScheduleRule = (rule, index, rulesDisabled) => {
      const headerRow = (text) => <Grid key={"row-" + text + "-headers"} item> <p> {text}</p> </Grid>
      if(!rule)
        return [headerRow("Delete"), headerRow("Enabled"), headerRow("Weekdays"), headerRow("Start time"), headerRow("Duration"), headerRow("Warning text")];
      const thisRuleDisabled = rulesDisabled || rule.enabled === false;

      return  <Grid container spacing={1} direction="row" alignItems="center" key={"schedule-row-render-" + index}>
                <Grid key={"row-" + index + "-1"} item>
                  <ListItemIcon id={"policy-list-item-icon"+index}>
                    <IconButton disabled={rulesDisabled} id={"policy-list-item-icon-button-"+index} onClick={() => this.deleteSchedule(index)} aria-label="Delete this item">
                      <Delete id={"policy-list-item-delete-"+index}/>
                    </IconButton>
                  </ListItemIcon>
                </Grid>
                <Grid item key={"row-" + index + "-2"}>
                  <Switch checked={rule.enabled || rule.enabled === undefined} disabled={rulesDisabled} onChange={this.updateScheduleLimit} color="primary" name={`schedule.limit|${index}|enabled`} />
                </Grid>
                <Grid item xs={4} key={"row-" + index + "-6"} className="mr-1">
                  <Grid container spacing={1} direction="row" key={"schedule-row-render-text-" + index} alignItems="center">
                    <Grid item xs={8} key={"row-" + index + "-6-1"}>
                      <TextField disabled={thisRuleDisabled} id={"policy-item-text-"+index} value={rule.text} onChange={this.updateScheduleLimit} type="text" name={"schedule.limits|" + index + "|text"} fullWidth variant="outlined" />
                    </Grid>
                    <Grid item xs={2} key={"row-" + index + "-6-2"}>
                      <TranslationEditor id={`limit-${index}-translation`} disabled={thisRuleDisabled} name={"schedule"} text={rule.text} i18={getTranslation("schedule", index)} updateTranslation={(i18) => this.updateTranslationEditor("schedule", i18, index)} />
                    </Grid>
                    <Grid item xs={2} key={"row-" + index + "-6-3"}>
                      <ConditionEditor id={`limit-${index}-conditions`} disabled={thisRuleDisabled} name={"Meeting Schedule Rule"} conditions={rule.conditions} updateConditions={(conditions) => this.updateConditionsEditor("schedule", conditions, index)} />
                    </Grid>

                  </Grid>
                </Grid>
                <Grid item key={"row-" + index + "-3"}>
                  <WeekdayPicker disabled={thisRuleDisabled} selectedDays={rule.weekday} name={"schedule.limits|" + index + "|weekday"} onChange={this.updateScheduleLimit} />
                </Grid>
                <Grid item key={"row-" + index + "-4"} xs={1}>
                  {startTimePicker(rule.startTime, "schedule.limits|" + index + "|startTime", thisRuleDisabled)}
                </Grid>
                <Grid item key={"row-" + index + "-5"}>
                  {durationPicker(rule.duration, "schedule.limits|" + index + "|duration", thisRuleDisabled)}
                </Grid>
                <Grid item xs={2} key={"row-" + index + "-7"}>
                  <TimezonePicker unselectLabel={"User Local Timezone"} value={rule.timezone ? rule.timezone : ""} disabled={thisRuleDisabled} onChange={this.updateScheduleLimit} type="timezone" name={"schedule.limits|" + index + "|timezone"} />
                </Grid>
              </Grid>
    }


    const getValueEditor = (rule, value, disabled) => {
      const id = "rule-item-text-"+ rule.name;
      const name = `rules.${rule.name}.value`
      if(rule.type === "boolean") {
        const opacity = disabled || !value ? "opacity-3" : ""
        return <div className={opacity}> { value ? "Enabled" : "Disabled"} </div>
      }
      else if(rule.type === "number")
        return  (
          <Grid container spacing={2} direction="row" alignItems="center">
            <Grid item xs={6}>
              <TextField disabled={disabled} id={id} value={value} onChange={this.updateValue} type="number" name={name} fullWidth variant="outlined" InputLabelProps={{shrink: true}} inputProps={{min:0}}/>
            </Grid>
          </Grid>
        )
      else if(rule.type === "minutes")
        return  (
          <Grid container spacing={2} direction="row" alignItems="center">
            <Grid item xs={6}>
              <TextField disabled={disabled} id={id} value={value} onChange={this.updateValue} type="number" name={name} fullWidth variant="outlined" InputLabelProps={{shrink: true}} inputProps={{min:0}}/>
            </Grid>
            <Grid item>
              {getHoursAndMinutes(value, disabled)}
            </Grid>
          </Grid>
        )
      else if(rule.type === "regex")
        return <TextField disabled={disabled} id={id} value={decodeURIComponent(value)} onChange={this.updateValue} type="regex" name={name} fullWidth variant="outlined" />
    }

    const getTranslation = (name, index) => {
      if(name === "header")
        return rules.i18 && rules.i18.header ? rules.i18.header : undefined;
      if(index === undefined && rules[name].i18)
        return rules[name].i18;
      if(index !== undefined && rules[name].limits[index].i18)
        return rules[name].limits[index].i18;
    }

    const getMandatoryToggle = (disabled, rule, i, index) => {

      const name = `rules.${i.name}.mandatory${index !== undefined ? "|" + index : ""}`;
      const onClick = () => {
        this.updateValue({target:{type: "boolean", name, value: !rule.mandatory}})
      }
      const props = {
        fontSize: "medium",
        className: "mr-2",
        onClick: disabled ? () => {} : onClick,
      }
      if(rule.mandatory)
        return <Block {...props} color={disabled ? "disabled" : "error"}/> 
      return <PauseCircleOutline {...props} color={disabled ? "disabled" : "inherit"}/> 
    }

    const renderRule = (i, index, rulesDisabled) => {
      const rule = rules[i.name];
      const ruleDescription = availableRules.find(r => r.name === i.name);
      const thisRuleDisabled = rulesDisabled || !rule || !rule.enabled;

      return  <Grid container spacing={1} direction="row" alignItems="center" key={"rule-row-render-"+index}>
                <Grid item key={"row-" + index + "-toggle"}>
                  <Switch checked={rule.enabled} disabled={rulesDisabled} onChange={this.updateValue} color="primary" name={`rules.${i.name}.enabled`} />
                </Grid>
                <Grid item key={"row-" + index + "-name"} xs={3} md={2}>
                  <Tooltip title={i.explain} className="mr-2">
                    <span>
                      <HelpOutline fontSize="small" className="mr-2"/>
                      {i.desc }
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item key={"row-" + index + "-mandatory"} >
                  <Tooltip title={`This rule is considered ${rule.mandatory ? "mandatory" : "suggestion"}. Depending on the status, rule either stops user from sending the invite, or allows them to proceed after reviewing the suggestions. Click the icon to change.`} className="mr-2">
                    { getMandatoryToggle(thisRuleDisabled, rule, i)}
                  </Tooltip>
                </Grid>
                <Grid item xs={2} key={"row-" + index + "-valueditor"}>
                  {getValueEditor(i, i.name === "shortening" ? rule.enabled : rule.value, thisRuleDisabled)}
                </Grid>
                <Grid item xs={4} key={"row-" + index + "-warning"}>
                  <TextField disabled={thisRuleDisabled} id={"policy-item-text-"+index} value={rule.text} onChange={this.updateValue} type="text" name={"rules." + i.name + ".text"} fullWidth variant="outlined" />
                </Grid>
                <Grid item key={"row-" + index + "-warning-translate-rule"}>
                  <TranslationEditor id={`row-${index}-translation`} disabled={thisRuleDisabled} name={i.name} text={rule.text} i18={getTranslation(i.name)} updateTranslation={(i18) => this.updateTranslationEditor(i.name, i18)} />
                </Grid>
                <Grid item key={"row-" + index + "-conditions"}>
                  <ConditionEditor id={`row-${index}-conditions`} disabled={thisRuleDisabled} name={ruleDescription.desc} conditions={rule.conditions} updateConditions={(conditions) => this.updateConditionsEditor(i.name, conditions)} />
                </Grid>
              </Grid>
    }


    const newScheduleRule = () =>
      <ListItem key={"policy-list-item-add"}>
        <ListItemIcon>
          <IconButton disabled={rulesDisabled} onClick={() => this.addSchedule()} aria-label="Add new item">
            <AddCircle  />
          </IconButton>
        </ListItemIcon>
      </ListItem>

    const rulesDisabled = this.props.disabled || !rules.enabled;
    const scheduleToggle = <Switch checked={rules.schedule.enabled} onChange={this.handleBooleanChange} disabled={rulesDisabled} color="primary" name="schedule.enabled" />
    const enableValidationRules = <Switch disabled={this.props.disabled} checked={rules.enabled} onChange={this.handleBooleanChange} color="primary" name="enabled" />

    return (
      <Fragment>
        <Grid item xs={12} key="meeting-validation-rules">
          { this.props.createHeader("Meeting Validation Rules", "These rules are controlling the dialog which is shown before user sends a calendar invite.", enableValidationRules) }
          <Grid container spacing={1} className="ml-3 mb-5">
            <Grid item xs={12}>
              <Grid container spacing={2} direction="column" >
                {this.props.createHeader("Rule Header", "Header for your meeting policy rules. i.e. Meeting Policy Hints")}
                <Grid item xs={6}>
                  <Grid container spacing={2} direction="row" alignItems="center">
                    <Grid item xs={10}>
                      <TextField id={"rule-header"} disabled={rulesDisabled} name={"rules.header"} type="text" onChange={this.updateValue} value={rules.header} fullWidth variant="outlined" />
                    </Grid>
                    <Grid item xs={2}>
                      <TranslationEditor id={`header-translation`} disabled={rulesDisabled} name={"header"} text={rules.header} i18={getTranslation("header")} updateTranslation={(i18) => this.updateTranslationEditor("header", i18)} />
                    </Grid>
                  </Grid>
                </Grid>
                {this.props.createHeader("Meeting Validation Rules", "Allows you to show a warning if these rules are being violated when user is about to send a calendar invite.")}
                {availableRules.map((rule, index) => renderRule(rule, index, rulesDisabled))}

              </Grid>
            </Grid>
          </Grid>
          {this.props.createHeader("Time Based Validation Rules", "Allows you to add any number of time validation rules to your meeting policy.", scheduleToggle)}
          <Grid container spacing={1} className="ml-3">
            <Grid item xs={12}>
              <Grid container spacing={2} direction="column" >
                {rules.schedule.limits.map((rule, index) => renderScheduleRule(rule, index, rulesDisabled || !rules.schedule.enabled))}
                { newScheduleRule() }
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Fragment>
    );
  }
}

export default AppSettingMeetingPolicy;
